import React, { useState, useMemo } from 'react';
import { DescriptionType, StepConfigType } from 'V2/Types';
import { Modal, Text, Controls } from 'V2/Shared/Modal';
import { Help } from 'V2/Shared/Icons/Help';
import styles from './styles.module.css';
import { Button } from 'V2/Shared/Button';
import classNames from 'classnames';
import informationIcon from 'V2/Shared/Assets/icon-information.svg';
import { parseReactElements } from 'V2/Utilities/ParseReactFromJson';

// TODO: find a less abstract way to handle step
// Title and SubTitle text; its too configurable
// for 1% of the time; we are using Title/SubTitle
// directly for the PhoneNumber step since we know their
// aren't any special configurations for title/sub-title
export const Title: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className={styles.item}>
    <div className={styles.title}>{children}</div>
  </div>
);

export const SubTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className={styles.subTitle}>{children}</div>;

export const InfoTitle: React.FC<{ title: string }> = ({ title }) => {
  return (
    <h3 className={styles.title} data-testid="nq-title">
      {title}
    </h3>
  );
};

export const InfoDescriptions: React.FC<{
  descriptions: DescriptionType[];
  cssOverrides?: { infoButton?: string };
}> = ({ descriptions, cssOverrides = {} }) => {
  const warnings = useMemo(
    () => descriptions.filter((d) => d.type.id === 'warning'),
    [descriptions]
  );

  const info = useMemo(
    () => descriptions.filter((d) => d.type.id === 'info'),
    [descriptions]
  );

  return (
    <div className={styles.item}>
      {info.map((item, i) => {
        const warning = warnings.find((d) => d.type.sub_type === 'info');

        if (item.type.sub_type === 'info_modal') {
          return (
            <RenderInfoModal
              key={`info-modal-${i}`}
              index={i}
              cssOverrides={cssOverrides}
              item={item}
            />
          );
        } else if (warning) {
          return (
            <RenderInfoWarningModal
              key={`info-modal-${i}`}
              index={i}
              warning={warning}
              cssOverrides={cssOverrides}
              item={item}
            />
          );
        } else {
          return renderInfo(i, item);
        }
      })}
    </div>
  );
};

export const InfoSection: React.FC<{
  config: StepConfigType;
  cssOverrides?: {
    infoButton?: string;
  };
}> = ({
  config: { title_text: title, step_descriptions: descriptions },
  cssOverrides = {},
}) => {
  return (
    <div data-testid="info-section">
      <InfoTitle title={title} />
      <InfoDescriptions
        descriptions={descriptions}
        cssOverrides={cssOverrides}
      />
    </div>
  );
};

export function renderInfo(
  i: number,
  item: DescriptionType,
  className?: string
): React.JSX.Element {
  return (
    <p
      className={className ? className : styles.subTitle}
      key={i}
      data-testid="nq-subtitle"
    >
      {item.label.replace(/\\n/g, '\n')}
    </p>
  );
}

const RenderInfoWarningModal: React.FC<{
  index: number;
  warning: DescriptionType;
  cssOverrides?: {
    infoButton?: string;
  };
  item: DescriptionType;
}> = ({ warning, index, cssOverrides, item }) => {
  const [showModal, setShowModal] = useState(false);
  const chat = warning.ctas.find((c) => c.type.id === 'chat_contact');

  return (
    <div key={index} data-testid={`info-section-${index}`}>
      <button
        className={classNames(styles.subTitle, cssOverrides?.infoButton)}
        type="button"
        onClick={() => setShowModal(true)}
        data-testid={`info-section-open-modal-${index}`}
      >
        {item.label}
        <Help alt="More Info" className="ml-8" testId="info" />
      </button>
      <Modal
        title={warning.label}
        showModal={showModal}
        closeAction={() => setShowModal(false)}
      >
        <Text>{warning.description}</Text>
        <Controls>
          <Button
            variant={chat ? 'outlined' : 'contained'}
            size="md"
            onClick={() => setShowModal(false)}
          >
            {chat ? 'Cancel' : 'Got It'}
          </Button>
        </Controls>
      </Modal>
    </div>
  );
};

const RenderInfoModal: React.FC<{
  index: number;
  cssOverrides?: {
    infoButton?: string;
  };
  item: DescriptionType;
}> = ({ index, cssOverrides, item }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div key={index} data-testid={`info-section-${index}`}>
      <button
        className={classNames(
          styles.subTitle,
          cssOverrides?.infoButton,
          styles.customInfoModal
        )}
        type="button"
        onClick={() => setShowModal(true)}
        data-testid={`info-section-open-modal-${index}`}
      >
        <div className={classNames(styles.centerContent)}>
          <img
            src={informationIcon}
            alt="Information"
            data-testid="Information"
            className={classNames(styles.customIcon)}
          />
          <span>{item.label}</span>
        </div>
      </button>
      <Modal
        title={item.modal_content?.[0]?.label || item.label}
        showModal={showModal}
        closeAction={() => setShowModal(false)}
        width="600px"
      >
        <Text>
          {item.modal_content?.[0].rich_text_content &&
            parseReactElements(
              item.modal_content?.[0].rich_text_content.content
            )}
        </Text>
        <Controls>
          <Button size="md" onClick={() => setShowModal(false)}>
            Okay
          </Button>
        </Controls>
      </Modal>
    </div>
  );
};
